import { Auth as amplifyAuth } from 'aws-amplify';
import Auth from './auth.common';
import { defer, Observable, switchMap, from, firstValueFrom } from 'rxjs';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { AuthService } from 'services/auth';
import initializeAxios from './axiosSetup';
import { axiosRequestConfiguration } from './axiosConfig';
import { map } from 'rxjs/operators';
import { merge } from 'lodash';
import { clearState as clearLocalStorageState } from 'localStorage';
import { isStoredSessionExpired, requestInactivityLogout } from './sessionExpiry';

const BaseUrl = '//' + __API__ + ':' + __APIPORT__ + '/api/';

const axiosInstance = initializeAxios(axiosRequestConfiguration);

interface IAuthOptions {
  headers: {
    Authorization?: string;
    awstoken?: string;
    casinoId?: string;
  };
}
const stripBearerPrefix = (token: string | null | undefined): string | null => {
  if (!token) return null;
  try {
    return token.replace(/^\s*Bearer\s+/i, '').trim();
  } catch (e) {
    return token;
  }
};

const normalizeAuthHeader = (headers: any, token: string | null) => {
  if (!headers) return;
  try {
    if (token) {
      const bare = stripBearerPrefix(token) || '';
      headers.Authorization = `Bearer ${bare}`;
      delete headers.authorization;
    } else {
      delete headers.Authorization;
      delete headers.authorization;
    }
  } catch (e) {
    // ignore
  }
};

const buildOptions = (
  options = {},
  isSecure?: boolean,
  awstoken?: string,
  isLambda?: boolean,
  casinoId?: string
): object => {
  let retOptions = {};
  if (isSecure) {
    const token = Auth.GetToken() as string;
    const headerToken = isLambda ? awstoken : token;
    const authOptions: IAuthOptions = {
      headers: {
        Authorization: `Bearer ${stripBearerPrefix(headerToken) || ''}`,
      },
    };

    if (isLambda) {
      authOptions.headers.awstoken = awstoken;
    }

    if (casinoId) {
      authOptions.headers.casinoId = casinoId;
    }
    retOptions = merge(options, authOptions);
    if ((retOptions as any).headers) {
      normalizeAuthHeader(
        (retOptions as any).headers,
        (authOptions.headers as any).Authorization || null
      );
    }
  } else {
    retOptions = { ...options };
  }
  return retOptions;
};

let isRefreshing = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];
const subscribeTokenRefresh = (cb: (token: string | null) => void) => refreshSubscribers.push(cb);
const onRefreshed = (token: string | null) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
  try {
    window.dispatchEvent(new CustomEvent('TokenRefreshed', { detail: token }));
  } catch (e) {
    // ignore
  }
};


const isTokenExpiredPayload = (data: any): boolean => {
  if (!data || typeof data !== 'object') return false;
  return (
    data.error === 'token_expired' ||
    data.err === 'token_expired' ||
    data.code === 'token_expired' ||
    (data.response &&
      (data.response.error === 'token_expired' || data.response.code === 'token_expired'))
  );
};

const retryRequestWithFreshToken = async (
  originalRequest: AxiosRequestConfig & { _retry?: boolean; headers?: any },
  fallbackError?: any
): Promise<AxiosResponse<any>> => {
  if (!originalRequest || originalRequest._retry) {
    return Promise.reject(fallbackError || new Error('token_expired'));
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      subscribeTokenRefresh((token) => {
        if (token) {
          originalRequest.headers = originalRequest.headers || {};
          normalizeAuthHeader(originalRequest.headers, token);
          axiosInstance(originalRequest).then(resolve).catch(reject);
        } else {
          reject(fallbackError || new Error('token_expired'));
        }
      });
    });
  }

  originalRequest._retry = true;
  isRefreshing = true;
  const newToken = await refreshAccessToken();
  isRefreshing = false;
  onRefreshed(newToken);

  if (newToken) {
    originalRequest.headers = originalRequest.headers || {};
    normalizeAuthHeader(originalRequest.headers, newToken);
    return axiosInstance(originalRequest);
  }

  try {
    await amplifyAuth.signOut();
  } catch (e) {}
  Auth.ClearJWKToken();
  Auth.ClearToken();
  try {
    clearLocalStorageState();
  } catch (e) {}
  window.location.replace('/logout');
  return Promise.reject(fallbackError || new Error('token_expired'));
};

const refreshAccessToken = async (): Promise<string | null> => {
  let refreshToken: string | null = window.localStorage.getItem('RefreshToken');
  if (!refreshToken) {
    try {
      const cognitoUser = await amplifyAuth.currentAuthenticatedUser();
      const currentSession = cognitoUser.getSignInUserSession();
      const refreshedSession = await new Promise<any>((resolve, reject) => {
        cognitoUser.refreshSession(
          currentSession.getRefreshToken(),
          (err: any, newSession: any) => {
            if (err || !newSession) {
              return reject(err);
            }
            resolve(newSession);
          }
        );
      });
      refreshToken = refreshedSession.getIdToken().getJwtToken();
    } catch (e) {}
  }

  if (!refreshToken) return null;

  try {
    const authService = new AuthService();
    const obs = authService.RefreshToken(refreshToken) as Observable<
      | void
      | { token: string; expiresIn?: number }
      | { code: number; response?: { token?: string; expiresIn?: number } }
    >;
    const payload = await firstValueFrom(obs);
    let newToken: string | undefined;

    if (payload) {
      if (typeof payload === 'object' && 'token' in payload && (payload as any).token) {
        newToken = (payload as any).token;
      } else {
        const maybeWrapped = payload as { code?: number; response?: { token?: string } };
        if (maybeWrapped.code === 200 && maybeWrapped.response && maybeWrapped.response.token) {
          newToken = maybeWrapped.response.token;
        }
      }
    }

    if (newToken) {
      Auth.SetJWKToken(newToken);
      Auth.SetToken(newToken);
      try {
        window.localStorage.setItem('Token', newToken);
      } catch (e) {}
      try {
        axiosInstance.defaults.headers = axiosInstance.defaults.headers || {};
        if (!axiosInstance.defaults.headers.common) axiosInstance.defaults.headers.common = {};
        normalizeAuthHeader(axiosInstance.defaults.headers.common, newToken);
      } catch (e) {}
      return newToken;
    }

    return null;
  } catch (err) {
    console.error('Failed to refresh token via AuthService', err);
    return null;
  }
};

axiosInstance.interceptors.response.use(
  async (resp: AxiosResponse<any>) => {
    try {
      const data = resp.data;
      if (data && typeof data === 'object') {
        // Some APIs return HTTP 200 with { isAuthorized:false, error:'token_expired' }
        // instead of 401 — refresh + retry once (app-wide), same as the error path.
        if (
          data.isAuthorized === false &&
          isTokenExpiredPayload(data) &&
          resp.config &&
          !(resp.config as any)._retry
        ) {
          return retryRequestWithFreshToken(
            resp.config as AxiosRequestConfig & { _retry?: boolean; headers?: any },
            new Error('token_expired')
          );
        }

        // Handle payloads like { isAuthorized: false, err: 'NO_ACCESS' }
        const isNoAccessPayload =
          data.isAuthorized === false &&
          (data.err === 'NO_ACCESS' ||
            data.error === 'NO_ACCESS' ||
            data.code === 'NO_ACCESS' ||
            (data.response && data.response.code === 'NO_ACCESS'));

        if (isNoAccessPayload) {
          try {
            await amplifyAuth.signOut();
          } catch (e) {
            // ignore
          }
          Auth.ClearJWKToken();
          Auth.ClearToken();
          try {
            clearLocalStorageState();
          } catch (e) {}
          // Route through /logout so Redux is cleared before redirecting to login.
          window.location.replace('/logout');
          return Promise.reject(new Error('NO_ACCESS'));
        }
      }
    } catch (e) {
      // ignore and continue
      console.log('Error processing response interceptor', e);
    }
    return resp;
  },
  async (error) => {
    const originalRequest = (error && error.config) as AxiosRequestConfig & {
      _retry?: boolean;
      headers?: any;
    };
    const response: AxiosResponse | undefined = error && error.response;
    const isTokenNoAccess = (() => {
      if (!response) return false;
      const status = response.status;
      const rdata = response.data as any;
      if (status === 403) {
        if (rdata && rdata.response && rdata.response.error === 'NO_ACCESS') return true;
        if (
          rdata &&
          (rdata.err === 'NO_ACCESS' ||
            rdata.error === 'NO_ACCESS' ||
            rdata.code === 'NO_ACCESS' ||
            (rdata.response && rdata.response.code === 'NO_ACCESS'))
        )
          return true;
      }
      return false;
    })();

    const isTokenExpiredError = (() => {
      if (!response) return false;
      const status = response.status;
      const rdata = response.data as any;
      // Prefer explicit token_expired (any status). Also treat bare 401 as expired.
      if (isTokenExpiredPayload(rdata)) return true;
      if (status === 401) return true;
      return false;
    })();

    if (isTokenExpiredError && originalRequest && !originalRequest._retry) {
      return retryRequestWithFreshToken(originalRequest, error);
    }

    if (isTokenNoAccess) {
      try {
        await amplifyAuth.signOut();
      } catch (e) {}
      Auth.ClearJWKToken();
      Auth.ClearToken();
      try {
        clearLocalStorageState();
      } catch (e) {}
      window.location.replace('/logout');
    }

    return Promise.reject(error);
  }
);

export const getAccessJwtToken = async () => {
  try {
    const session = await amplifyAuth.currentSession();
    const idToken = session.getIdToken();
    const jwtToken = idToken.getJwtToken();
    const tokenPayload = JSON.parse(atob(jwtToken.split('.')[1]));
    const expiryTime = tokenPayload.exp * 1000;
    const currentTime = Date.now();
    if (currentTime >= expiryTime) {
      try {
        const cognitoUser = await amplifyAuth.currentAuthenticatedUser();
        const currentSession = cognitoUser.getSignInUserSession();
        const refreshedSession = await new Promise<any>((resolve, reject) => {
          cognitoUser.refreshSession(
            currentSession.getRefreshToken(),
            (err: any, newSession: any) => {
              if (err || !newSession) {
                return reject(err);
              }
              resolve(newSession);
            }
          );
        });
        const newJwtToken = refreshedSession.getIdToken().getJwtToken();
        Auth.SetJWKToken(newJwtToken);
        return newJwtToken;
      } catch (refreshError) {
        await amplifyAuth.signOut();
        clearLocalStorageState();
        window.location.replace('/logout');
        return null;
      }
    }

    Auth.SetJWKToken(jwtToken);
    return jwtToken;
  } catch (error) {
    await amplifyAuth.signOut();
    clearLocalStorageState();
    window.location.replace('/logout');
    return null;
  }
};

const checkAuth = (
  options: object,
  isSecure?: boolean,
  isLambda?: boolean,
  casinoId?: string
): Promise<object> => {
  return new Promise((resolve, reject) => {
    // Inactivity logout (lib/sessionExpiry): no secure call goes out once the limit has
    // passed, e.g. a request fired by a screen that resumed after the phone was locked.
    if (isStoredSessionExpired(Date.now())) {
      requestInactivityLogout();
      return reject(new Error('session_expired'));
    }
    getAccessJwtToken()
      .then((awsToken) => {
        if (!awsToken) {
          return reject(new Error('Authentication token is missing or expired'));
        }
        resolve(buildOptions(options, isSecure, awsToken, isLambda, casinoId));
      })
      .catch((err) => reject(err));
  });
};

const postSecure = <T>(
  url: string,
  body: object,
  options = {},
  isLambda = false,
  casinoId?: string
): Observable<T | void> => {
  return from(checkAuth(options, true, isLambda, casinoId)).pipe(
    switchMap((builtOptions) => post<T>(url, body, builtOptions))
  );
};

const post = <T>(url: string, body: object, options = {}): Observable<T | void> => {
  return defer(() => axiosInstance.post<T>(url, body, options)).pipe(map((result) => result.data));
};

const Post = <T>(
  url: string,
  body: object,
  options = {},
  isSecure = false,
  isLambda = false,
  casinoId?: string
) => {
  if (isSecure) {
    return postSecure<T>(url, body, options, isLambda, casinoId);
  } else {
    return post<T>(url, body, options);
  }
};

const get = <T>(url: string, queryParams?: object, options?: object): Observable<T> => {
  // params must win over options — spreading options after params previously
  // allowed a merged `params` key to drop query filters (e.g. q / manufacturer).
  const config = {
    ...options,
    params: queryParams,
  };
  return defer(() => axiosInstance.get<T>(url, config)).pipe(map((result) => result.data));
};

const getSecure = <T>(
  url: string,
  queryParams: object,
  options = {},
  isLambda = false,
  casinoId?: string
): Observable<T | void> => {
  return from(checkAuth(options, true, isLambda, casinoId)).pipe(
    switchMap((builtOptions) => get<T>(url, queryParams, builtOptions))
  );
};

const Get = <T>(
  url: string,
  queryParams: object,
  options = {},
  isSecure = false,
  isLambda = false,
  casinoId?: string
) => {
  if (isSecure) {
    return getSecure<T>(url, queryParams, options, isLambda, casinoId);
  } else {
    return get<T>(url, queryParams, options);
  }
};

const put = <T>(url: string, body: object, queryParams?: object): Observable<T | void> => {
  return defer(() => axiosInstance.put<T>(`${BaseUrl}${url}`, body, { params: queryParams })).pipe(
    map((result) => result.data)
  );
};

const patch = <T>(url: string, body: object, queryParams?: object): Observable<T | void> => {
  return defer(() =>
    axiosInstance.patch<T>(`${BaseUrl}${url}`, body, { params: queryParams })
  ).pipe(map((result) => result.data));
};

export default { Get, Post, put, patch };
export { Post, Get };
